import { type FC, type ReactNode } from 'react'
import { Sticky } from '../../../components/Sticky'
import {
  type BookingClient,
  type BookingInvitee,
  type EmailTemplatePurpose,
  type Location,
  type MeetingMode,
  type MeetingPlatformProvider,
  type SchedulingEventType,
} from '../../../models/Scheduling'
import { type User } from '../../../models/User'
import { type LocationInput } from '../../../store/scheduling/dto'
import { CalendarInvitePreview } from './CalendarInvitePreview'
import { type TimeSelectionMode } from './form/SetTimeSection'
import { type BookingSlot } from './form/timeSlots'
import { InvitationEmailPreview } from './InvitationEmailPreview'

interface SchedulePreviewColumnProps {
  eventType?: SchedulingEventType
  mode: TimeSelectionMode
  emptyState: ReactNode
  emailPurpose: EmailTemplatePurpose
  owner?: User
  ownerName: string
  invitees: BookingInvitee[]
  meetingMode: MeetingMode
  meetingPlatform?: MeetingPlatformProvider | null
  joinLinkBroken?: boolean
  slot: BookingSlot | null
  title: string
  timeZone?: string
  timezoneLabel: string
  location: Location | LocationInput | null
  note: string
  onNoteChange: (note: string) => void
  clients: BookingClient[]
  bookingLink?: string
  originalStart?: string
  onCopyEmail: (client?: BookingClient) => void
  copyEmailDisabled?: boolean
  copyEmailDisabledReason?: string
}

// Right column shared by the schedule and reschedule flows: an empty state until an event type is
// known, then the calendar invite (specific time) or the invitation email (client chooses).
export const SchedulePreviewColumn: FC<SchedulePreviewColumnProps> = ({
  eventType,
  mode,
  emptyState,
  emailPurpose,
  owner,
  ownerName,
  invitees,
  meetingMode,
  meetingPlatform,
  joinLinkBroken = false,
  slot,
  title,
  timeZone,
  timezoneLabel,
  location,
  note,
  onNoteChange,
  clients,
  bookingLink,
  originalStart,
  onCopyEmail,
  copyEmailDisabled = false,
  copyEmailDisabledReason,
}) => {
  if (!eventType) {
    return <>{emptyState}</>
  }
  if (mode === 'specific-time') {
    return (
      <Sticky mode='top'>
        <CalendarInvitePreview
          eventType={eventType}
          ownerName={ownerName}
          invitees={invitees}
          meetingMode={meetingMode}
          meetingPlatform={meetingPlatform}
          joinLinkBroken={joinLinkBroken}
          slot={slot}
          title={title}
          timeZone={timeZone}
          timezoneLabel={timezoneLabel}
          location={location}
          note={note}
          onNoteChange={onNoteChange}
        />
      </Sticky>
    )
  }
  return (
    <Sticky mode='top'>
      <InvitationEmailPreview
        purpose={emailPurpose}
        eventType={eventType}
        clients={clients}
        owner={owner}
        ownerName={ownerName}
        meetingMode={meetingMode}
        location={location}
        bookingLink={bookingLink}
        originalStart={originalStart}
        onCopyEmail={onCopyEmail}
        copyDisabled={copyEmailDisabled}
        copyDisabledReason={copyEmailDisabledReason}
      />
    </Sticky>
  )
}
